import { useState, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { getUsersName } from 'services/users-service';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { InputLoader } from 'Components/PageLoader/Input';
import { Checkbox, Radio, Select, TextField } from 'Components/ui';
import {
  FormWrap, FormCard, FormCardHeader, FormCardIcon, FormCardHeaderText,
  FormCardTitle, FormCardSubtitle,
  FormFieldGrid, FormFieldFull, FormFieldHalf, FormLabel,
  FormFieldError,
  FormTagBox, FormTagChip, FormTagChipX, FormTagInlineInput,
  FormImpactGrid, FormImpactField, FormImpactTitle, FormImpactDesc,
  FormRadioGroup, FormRadioLabel,
  FormVulnerableBox, FormVulnerableTitle, FormVulnerableDesc,
} from '../../styles';

const FileIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

export const Detalhes = ({ handleNext, register, control, handleSubmit, errors, getValues, id }) => {
  const { t } = useTranslation('mapping');
  const selectedTenant = useSelector(useAuthTenant);
  const [tagInput, setTagInput] = useState('');
  const tagInputRef = useRef(null);

  const onSubmit = () => handleNext();

  const { data: users, isLoading: userLoading } = useQuery(['getUsers'], async () => {
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  return (
    <form id="proc-form" onSubmit={handleSubmit(onSubmit)}>
      <FormWrap>
        {/* Card 1 – Informações Gerais */}
        <FormCard>
          <FormCardHeader>
            <FormCardIcon $gradient="linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))">
              <FileIcon />
            </FormCardIcon>
            <FormCardHeaderText>
              <FormCardTitle>{t('processes.form.card.generalInfo')}</FormCardTitle>
              <FormCardSubtitle>{t('processes.form.card.generalInfoSub')}</FormCardSubtitle>
            </FormCardHeaderText>
          </FormCardHeader>

          <FormFieldGrid>
            <FormFieldFull>
              <FormLabel htmlFor="proc-name" $required>{t('processes.form.fields.name')}</FormLabel>
              <TextField
                id="proc-name"
                fullWidth
                autoFocus
                data-tour="process-name"
                error={!!errors?.name}
                disabled={!!id}
                placeholder={t('processes.form.placeholders.name')}
                {...register('name', { required: t('processes.form.validation.nameRequired') })}
              />
              {errors?.name && <FormFieldError>{errors.name.message}</FormFieldError>}
            </FormFieldFull>

            <FormFieldFull data-tour="process-tags">
              <FormLabel>{t('processes.form.fields.tags')}</FormLabel>
              <Controller
                control={control}
                name="tags"
                render={({ field: { onChange, value } }) => {
                  const tags = Array.isArray(value) ? value : [];
                  const addTag = raw => {
                    const tag = raw.trim();
                    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
                  };
                  return (
                    <FormTagBox onClick={() => tagInputRef.current?.focus()}>
                      {tags.map(tag => (
                        <FormTagChip key={tag}>
                          {tag}
                          <FormTagChipX type="button" onClick={e => { e.stopPropagation(); onChange(tags.filter(tg => tg !== tag)); }}>×</FormTagChipX>
                        </FormTagChip>
                      ))}
                      <FormTagInlineInput
                        ref={tagInputRef}
                        value={tagInput}
                        placeholder={tags.length === 0 ? t('processes.form.placeholders.tags') : ''}
                        onChange={e => setTagInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            addTag(tagInput);
                            setTagInput('');
                          } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
                            onChange(tags.slice(0, -1));
                          }
                        }}
                        onBlur={() => { if (tagInput.trim()) { addTag(tagInput); setTagInput(''); } }}
                      />
                    </FormTagBox>
                  );
                }}
              />
            </FormFieldFull>

            <FormFieldFull>
              <FormLabel htmlFor="proc-desc" $required>{t('processes.form.fields.description')}</FormLabel>
              <TextField
                id="proc-desc"
                multiline
                fullWidth
                rows={4}
                data-tour="process-description"
                placeholder={t('processes.form.placeholders.description')}
                error={!!errors?.description}
                {...register('description', { required: t('processes.form.validation.descriptionRequired') })}
              />
              {errors?.description && <FormFieldError>{errors.description.message}</FormFieldError>}
            </FormFieldFull>

            <FormFieldFull>
              <FormLabel htmlFor="proc-flow" $required>{t('processes.form.fields.descriptionDataFlow')}</FormLabel>
              <TextField
                id="proc-flow"
                multiline
                fullWidth
                rows={4}
                data-tour="process-dataflow"
                placeholder={t('processes.form.placeholders.dataFlow')}
                error={!!errors?.descriptionDataFlow}
                {...register('descriptionDataFlow', { required: t('processes.form.validation.dataFlowRequired') })}
              />
              {errors?.descriptionDataFlow && <FormFieldError>{errors.descriptionDataFlow.message}</FormFieldError>}
            </FormFieldFull>

            <FormFieldHalf data-tour="process-criticity">
              <FormLabel htmlFor="proc-crit" $required>{t('processes.form.fields.criticality')}</FormLabel>
              <Controller
                control={control}
                name="criticity"
                rules={{ validate: () => getValues('criticity') !== '', required: t('processes.form.validation.criticalityRequired') }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    name="criticity"
                    value={value}
                    onChange={onChange}
                    error={!!errors?.criticity}
                    options={[
                      { value: '', label: t('processes.form.placeholders.selectCriticality') },
                      ...['1', '2', '3', '4', '5'].map(v => ({ value: v, label: t('processes.form.criticityLabels.' + v) })),
                    ]}
                  />
                )}
              />
              {errors?.criticity && <FormFieldError>{errors.criticity.message}</FormFieldError>}
            </FormFieldHalf>

            <FormFieldHalf data-tour="process-user">
              <FormLabel htmlFor="proc-user" $required>{t('processes.form.fields.responsible')}</FormLabel>
              {userLoading ? (
                <InputLoader />
              ) : (
                <Controller
                  control={control}
                  name="user"
                  rules={{ validate: () => getValues('user') !== '', required: t('processes.form.validation.responsibleRequired') }}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="user"
                      value={value}
                      onChange={onChange}
                      error={!!errors?.user}
                      options={[
                        { value: '', label: t('processes.form.placeholders.selectResponsible') },
                        ...(users || []).map(u => ({ value: u.name, label: u.name })),
                      ]}
                    />
                  )}
                />
              )}
              {errors?.user && <FormFieldError>{errors.user.message}</FormFieldError>}
            </FormFieldHalf>
          </FormFieldGrid>
        </FormCard>

        {/* Card 2 – Avaliações de Impacto */}
        <FormCard>
          <FormCardHeader>
            <FormCardIcon $gradient="linear-gradient(135deg,var(--vo-danger),var(--vo-warning))">
              <ShieldIcon />
            </FormCardIcon>
            <FormCardHeaderText>
              <FormCardTitle>{t('processes.form.card.impact')}</FormCardTitle>
              <FormCardSubtitle>{t('processes.form.card.impactSub')}</FormCardSubtitle>
            </FormCardHeaderText>
          </FormCardHeader>

          <FormImpactGrid>
            <FormImpactField>
              <FormImpactTitle>{t('processes.form.fields.dpiaTitle')}</FormImpactTitle>
              <FormImpactDesc>{t('processes.form.fields.dpiaDesc')}</FormImpactDesc>
              <Controller
                control={control}
                name="dpia"
                render={({ field: { onChange, value } }) => (
                  <FormRadioGroup>
                    <FormRadioLabel>
                      <Radio name="dpia" checked={value === true} onChange={() => onChange(true)} />
                      {t('processes.form.radio.yes')}
                    </FormRadioLabel>
                    <FormRadioLabel>
                      <Radio name="dpia" checked={!value} onChange={() => onChange(false)} />
                      {t('processes.form.radio.no')}
                    </FormRadioLabel>
                  </FormRadioGroup>
                )}
              />
            </FormImpactField>

            <FormImpactField>
              <FormImpactTitle>{t('processes.form.fields.liaTitle')}</FormImpactTitle>
              <FormImpactDesc>{t('processes.form.fields.liaDesc')}</FormImpactDesc>
              <Controller
                control={control}
                name="lia"
                render={({ field: { onChange, value } }) => (
                  <FormRadioGroup>
                    <FormRadioLabel>
                      <Radio name="lia" checked={value === true} onChange={() => onChange(true)} />
                      {t('processes.form.radio.yes')}
                    </FormRadioLabel>
                    <FormRadioLabel>
                      <Radio name="lia" checked={!value} onChange={() => onChange(false)} />
                      {t('processes.form.radio.no')}
                    </FormRadioLabel>
                  </FormRadioGroup>
                )}
              />
            </FormImpactField>
          </FormImpactGrid>

          <Controller
            control={control}
            name="dataTreatmentFromChildrenTeenagerElderly"
            render={({ field: { onChange, value } }) => (
              <FormVulnerableBox data-tour="process-checkboxes">
                <Checkbox checked={!!value} onChange={onChange} name="dataTreatmentFromChildrenTeenagerElderly" />
                <div>
                  <FormVulnerableTitle>{t('processes.form.fields.vulnerableTitle')}</FormVulnerableTitle>
                  <FormVulnerableDesc>{t('processes.form.fields.vulnerableDesc')}</FormVulnerableDesc>
                </div>
              </FormVulnerableBox>
            )}
          />
        </FormCard>
      </FormWrap>
    </form>
  );
};
